import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from './AuthProvider'
import { getMe } from '../api/settings'
import type { AppStatus, MeResponse, MissingCredential } from '../types'

/**
 * The gate.
 *
 * Resolves auth + credential state into exactly one view. Nothing in the app
 * toggles views manually — settings is simply what you get until setup is done.
 */
/** How long a focus check stays good for. */
const FOCUS_MIN_GAP_MS = 60_000;

export function useAppState() {
  const { user, loading: authLoading } = useAuth()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState('')

  const refresh = useCallback(async () => {
    if (!user) {
      setMe(null)
      return
    }
    setChecking(true)
    setError('')
    try {
      setMe(await getMe())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setMe(null)
    } finally {
      setChecking(false)
    }
  }, [user])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Zoho is connected in a separate tab, so the panel cannot be notified when
  // it finishes. Re-checking on focus means the common case needs no manual
  // refresh button press.
  //
  // Throttled, because inside the Desk widget an agent clicks between Desk and
  // the panel constantly and every one of those was a full `me` — an Auth
  // lookup plus two Firestore reads, one of them carrying the profile picture.
  // A credential does not change second to second.
  //
  // But only once setup is finished. While a credential is still missing, the
  // agent is most likely in another tab granting it, and coming back is the
  // one moment a re-check must not be skipped — which the throttle did, so an
  // agent who had just approved Zoho returned to a panel still saying Connect.
  const lastCheck = useRef(0)
  const settled = useRef(false)
  settled.current = Boolean(me?.zoho.valid && me?.providers?.[me.draftProvider]?.valid)

  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState === 'hidden') return
      if (settled.current && Date.now() - lastCheck.current < FOCUS_MIN_GAP_MS) return
      lastCheck.current = Date.now()
      void refresh()
    }

    // Both: inside Desk's iframe, switching tabs reliably fires one or the
    // other, not always the same one.
    window.addEventListener('focus', onReturn)
    document.addEventListener('visibilitychange', onReturn)
    return () => {
      window.removeEventListener('focus', onReturn)
      document.removeEventListener('visibilitychange', onReturn)
    }
  }, [refresh])

  // Two things to finish, not three: searching the knowledge base runs on the
  // server's own key, so the only API key an agent supplies is for the model
  // that writes the reply.
  const missing: MissingCredential[] = []
  if (me) {
    if (!me.zoho.valid) missing.push('zoho')
    if (!me.providers?.[me.draftProvider]?.valid) missing.push('draft-model')
  }

  let status: AppStatus = 'loading'
  if (authLoading || (user && !me && checking)) status = 'loading'
  else if (!user) status = 'signed-out'
  else if (!me || missing.length) status = 'setup-required'
  else status = 'ready'

  return { status, me, missing, error, refresh }
}
